import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Spinner, Form, Col, Row, Badge } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showDeleteConfirmation } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import ViewKeyModule from './ViewKeyModule';
import UpdateKeyModule from './UpdateKeyModule';
import UpdateKeysModule from './UpdateKeysModule';

export interface KeyDTO {
  id: string;
  keyCode: string;
  doorId: string | null;
  roomId: string | null;
  roomName: string | null;
  floorId: string | null;
  floorName: string | null;
  buildingId: string | null;
  buildingName: string | null;
  vehicleId: string | null;
  vehicleNumberPlate: string | null;
  noOfKeys: number;
  separatedKeys: number | 0;
  bundles: { count: number }[] | 0;
  responsibleParty: string | null;
  isRestricted: boolean;
  handoverStatus: string | null;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface VehicleDTO {
  vehicleId: string;
  vehicleType: string;
  vehicleBrand: string;
  vehicleModel: string;
  numberPlate: string;
  vehicleStatus: string;
}

interface ApiResponse<T> {
  status: string;
  statusDescription: string;
  data: T;
}

interface VehicleKeyModuleProps {
  show: boolean;
  onHide: () => void;
  numberPlate?: string;
  vehicleId: string;
  onKeyAdded?: () => void;
}

const formatDateTime = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '-');

const statusVariant = (s: string) => {
  switch (s) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'secondary';
    default:
      return 'info';
  }
};

const VehicleKeyModule: React.FC<VehicleKeyModuleProps> = ({ show, onHide,numberPlate, vehicleId, onKeyAdded }) => {
  const [keys, setKeys] = useState<KeyDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddKeyForm, setShowAddKeyForm] = useState(false);
  const [showViewKey, setShowViewKey] = useState(false);
  const [showUpdateKey, setShowUpdateKey] = useState(false);
  const [showUpdateKeys, setShowUpdateKeys] = useState(false);
  const [selectedKey, setSelectedKey] = useState<KeyDTO | null>(null);
  const [formData, setFormData] = useState({
    separatedKeys: 0,
    bundles: [{ count: 1 }],
    responsibleParty: localStorage.getItem('username'),
    isRestricted: false,
    handoverStatus: 'PENDING',
    status: 'ACTIVE',
  });

  const noOfKeys = (formData.separatedKeys || 0) + formData.bundles.reduce((sum, bundle) => sum + (bundle.count || 0), 0);

  const fetchKeys = async () => {
    if (!checkPermission('ROLE_GET_KEYS')) {
      showError();
      return;
    }
    setIsLoading(true);
    try {
      const res = await apiDefinitions.getKeysByVehicleId(vehicleId);
      if (res.status === 200 && res.data.status === 'S1000') {
        const data: KeyDTO[] = res.data.data || [];
        setKeys(data);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch keys');
      }
    } catch (e: any) {
      showError('Failed to fetch keys. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchKeyById = async (keyId: string) => {
    if (!checkPermission('ROLE_GET_KEYS')) {
      showError('You do not have permission to fetch key details.');
      return;
    }
    setIsLoading(true);
    try {
      const res = await apiDefinitions.getKeyById(keyId);
      if (res.status === 200 && res.data.status === 'S1000') {
        const keyData: KeyDTO = res.data.data;
        setSelectedKey(keyData);
        setShowUpdateKey(true);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch key details');
      }
    } catch (e: any) {
      showError('Failed to fetch key details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show) fetchKeys();
  }, [show, vehicleId]);

  const handleAddBundle = () => {
    setFormData({
      ...formData,
      bundles: [...formData.bundles, { count: 1 }],
    });
  };

  const handleRemoveBundle = (index: number) => {
    setFormData({
      ...formData,
      bundles: formData.bundles.filter((_, i) => i !== index),
    });
  };

  const handleBundleCountChange = (index: number, value: number) => {
    const updatedBundles = [...formData.bundles];
    updatedBundles[index] = { count: Math.max(1, value) };
    setFormData({ ...formData, bundles: updatedBundles });
  };

  const handleAddKey = async () => {
    if (!checkPermission('ROLE_ADD_KEY')) {
      showError('You do not have permission to add keys.');
      return;
    }
    if (noOfKeys <= 0) {
      showError('Total keys must be greater than 0.');
      return;
    }
    if (formData.bundles.some(bundle => bundle.count <= 0)) {
      showError('Each bundle must have at least 1 key.');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        vehicleId: String(vehicleId),
        doorId: null,
        roomId: null,
        noOfKeys,
        separatedKeys: formData.separatedKeys || 0,
        bundles: formData.bundles.map(bundle => ({ count: bundle.count })),
        responsibleParty: formData.responsibleParty || null,
        isRestricted: formData.isRestricted,
        handoverStatus: formData.handoverStatus || null,
        status: formData.status,
      };
      const res = await apiDefinitions.addKey(payload);
      if (res.status === 200 && res.data.status === 'S1000') {
        setShowAddKeyForm(false);
        setFormData({
          separatedKeys: 0,
          bundles: [{ count: 1 }],
          responsibleParty: '',
          isRestricted: false,
          handoverStatus: 'PENDING',
          status: 'ACTIVE',
        });
        await fetchKeys();
        onKeyAdded?.();
      } else {
        showError(res.data?.statusDescription || 'Failed to add key');
      }
    } catch (e: any) {
      showError(`Failed to add key: ${e.response?.data?.statusDescription || e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!checkPermission('ROLE_UPDATE_KEY')) {
      showError('You do not have permission to delete keys.');
      return;
    }
    showDeleteConfirmation(async () => {
      setIsLoading(true);
      try {
        const res = await apiDefinitions.deleteKey(keyId);
        if (res.status === 200 && res.data.status === 'S1000') {
          await fetchKeys();
          setSelectedKey(null);
          onKeyAdded?.();
        } else {
          showError(res.data?.statusDescription || 'Failed to delete key');
        }
      } catch (e: any) {
        showError(`Failed to delete key: ${e.response?.data?.statusDescription || e.message}`);
      } finally {
        setIsLoading(false);
      }
    });
  };

  const handleViewKey = (key: KeyDTO) => {
    setSelectedKey(key);
    setShowViewKey(true);
  };

  const handleEditKey = (key: KeyDTO) => {
    fetchKeyById(key.id);
  };

  const handleUpdateKeys = () => {
    if (!checkPermission('ROLE_UPDATE_KEY')) {
      showError('You do not have permission to update keys.');
      return;
    }
    if (keys.length === 0) {
      showError('No keys available to update.');
      return;
    }
    setShowUpdateKeys(true);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Keys for Vehicle ID: {numberPlate}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showAddKeyForm ? (
            <Form>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group controlId="noOfKeys">
                    <Form.Label>Total Keys</Form.Label>
                    <Form.Control type="number" min="0" value={noOfKeys} readOnly />
                    <small className="text-muted">Auto-calculated as Separated + sum(Bundle counts)</small>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="separatedKeys">
                    <Form.Label>Separated Keys</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={formData.separatedKeys}
                      onChange={(e) => setFormData({ ...formData, separatedKeys: parseInt(e.target.value) || 0 })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Label>Bundles</Form.Label>
                  {formData.bundles.map((bundle, index) => (
                    <Row key={index} className="mb-2 align-items-center">
                      <Col xs={8}>
                        <Form.Group controlId={`bundle-${index}`}>
                          <Form.Control
                            type="number"
                            min="1"
                            placeholder={`Keys in bundle ${index + 1}`}
                            value={bundle.count}
                            onChange={(e) => handleBundleCountChange(index, parseInt(e.target.value) || 1)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={4}>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveBundle(index)}
                          disabled={formData.bundles.length === 0}
                        >
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button variant="outline-primary" size="sm" onClick={handleAddBundle} className="mt-2">
                    Add Bundle
                  </Button>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group controlId="handoverStatus">
                    <Form.Label>Handover Status</Form.Label>
                    <Form.Control
                      as="select"
                      value={formData.handoverStatus}
                      onChange={(e) => setFormData({ ...formData, handoverStatus: e.target.value })}
                    >
                      <option value="PENDING">PENDING</option>
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                      <option value="">Not Set</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="status">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="isRestricted">
                    <Form.Check
                      type="checkbox"
                      label="Restricted"
                      checked={formData.isRestricted}
                      onChange={(e) => setFormData({ ...formData, isRestricted: e.target.checked })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="secondary"
                  className="me-2"
                  onClick={() => setShowAddKeyForm(false)}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button variant="success" onClick={handleAddKey} disabled={isLoading}>
                  {isLoading ? <Spinner size="sm" /> : 'Add Key'}
                </Button>
              </div>
            </Form>
          ) : (
            <>
              <div className="d-flex justify-content-end mb-3 gap-2">
                {keys.length === 0 && (
                  <Button
                    variant="success"
                    onClick={() => setShowAddKeyForm(true)}
                    disabled={isLoading || !checkPermission('ROLE_ADD_KEY')}
                  >
                    Add Key
                  </Button>
                )}
                {keys.length > 0 && (
                  <Button
                    variant="success"
                    onClick={handleUpdateKeys}
                    disabled={isLoading || !checkPermission('ROLE_UPDATE_KEY')}
                  >
                    Update All Keys
                  </Button>
                )}
              </div>
              {isLoading ? (
                <div className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading keys...</span>
                  </Spinner>
                </div>
              ) : keys.length === 0 ? (
                <div className="text-muted">No keys available for this vehicle.</div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Key Code</th>
                      <th>Keys</th>
                      <th>Responsible Party</th>
                      <th>Restricted</th>
                      <th>H_Status</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((key) => (
                      <tr key={key.id}>
                        <td>{key.id}</td>
                        <td>{key.keyCode}</td>
                        <td>{key.noOfKeys}</td>
                        <td>{key.responsibleParty ?? '-'}</td>
                        <td>{key.isRestricted ? 'Yes' : 'No'}</td>
                        <td>{key.handoverStatus ?? '-'}</td>
                        <td>
                          <Badge bg={statusVariant(key.status)}>{key.status}</Badge>
                        </td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-1 rounded-circle"
                            onClick={() => handleViewKey(key)}
                            title="View Key"
                          >
                            <i className="feather-eye" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="me-1 rounded-circle"
                            onClick={() => handleEditKey(key)}
                            title="Edit Key"
                            disabled={!checkPermission('ROLE_UPDATE_KEY')}
                          >
                            <i className="feather-edit" />
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            className="rounded-circle"
                            onClick={() => handleDeleteKey(key.id)}
                            title="Delete Key"
                            disabled={!checkPermission('ROLE_UPDATE_KEY')}
                          >
                            <i className="feather-trash" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={isLoading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {selectedKey && (
        <ViewKeyModule
          show={showViewKey}
          onHide={() => {
            setShowViewKey(false);
            setSelectedKey(null);
          }}
          keyData={selectedKey}
        />
      )}
      {selectedKey && (
        <UpdateKeyModule
          show={showUpdateKey}
          onHide={() => {
            setShowUpdateKey(false);
            setSelectedKey(null);
          }}
          keyData={selectedKey}
          doorId={0} // Not used for vehicle keys
          roomId={0} // Not used for vehicle keys
          onKeyUpdated={() => {
            fetchKeys();
            onKeyAdded?.();
          }}
        />
      )}
      <UpdateKeysModule
        show={showUpdateKeys}
        onHide={() => setShowUpdateKeys(false)}
        keys={keys}
        doorId={0} // Not used for vehicle keys
        roomId={0} // Not used for vehicle keys
        onKeysUpdated={() => {
          fetchKeys();
          onKeyAdded?.();
        }}
      />
    </>
  );
};

export default VehicleKeyModule;